import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  UsersIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const formatISO = (d) => d.toISOString().slice(0, 10);

export default function EnrollmentForecastPage() {
  const [weekStart, setWeekStart] = useState(formatISO(startOfWeek(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});  // { classroomId|date: kids }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/enrollment-forecast?start=${weekStart}`);
      setData(data);
      const d = {};
      for (const row of data.rows) {
        for (const cell of row.cells) {
          d[`${row.id}|${cell.date}`] = cell.expected_kids ?? '';
        }
      }
      setDraft(d);
    } catch { toast.error('Failed to load forecast'); }
    finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const setCell = (classroomId, date, value) => {
    setDraft({ ...draft, [`${classroomId}|${date}`]: value });
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const entries = [];
      for (const row of data.rows) {
        for (const cell of row.cells) {
          const key = `${row.id}|${cell.date}`;
          const current = cell.expected_kids ?? '';
          const next = draft[key] ?? '';
          if (String(current) !== String(next)) {
            entries.push({
              classroom_id: row.id,
              date: cell.date,
              expected_kids: next === '' ? null : Number(next),
            });
          }
        }
      }
      if (entries.length === 0) {
        toast('No changes to save');
        setSaving(false);
        return;
      }
      await api.post('/enrollment-forecast', { entries });
      toast.success(`Saved ${entries.length} change${entries.length === 1 ? '' : 's'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const weekStartDate = new Date(weekStart);
  const monthLabel = weekStartDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Check if any cells have changed (for save button enable state)
  const hasChanges = data.rows.some(row =>
    row.cells.some(cell => String(cell.expected_kids ?? '') !== String(draft[`${row.id}|${cell.date}`] ?? ''))
  );

  return (
    <div className="animate-slide-up">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <UsersIcon className="w-6 h-6" /> Enrollment Forecast
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Expected number of children per classroom per day. The AI scheduler uses these to know how many staff to assign.
          Manual entry for now — once bbc-booking is wired, real enrollment data syncs automatically.
        </p>
      </header>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart(formatISO(addDays(weekStartDate, -7)))}
            className="p-2 rounded-lg hover:bg-gray-100"><ChevronLeftIcon className="w-4 h-4" /></button>
          <p className="text-sm font-semibold text-gray-900 min-w-[200px] text-center">
            Week of {weekStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {monthLabel}
          </p>
          <button onClick={() => setWeekStart(formatISO(addDays(weekStartDate, 7)))}
            className="p-2 rounded-lg hover:bg-gray-100"><ChevronRightIcon className="w-4 h-4" /></button>
          <button onClick={() => setWeekStart(formatISO(startOfWeek(new Date())))}
            className="text-xs text-gray-500 hover:text-orange-500 ml-2">This week</button>
        </div>
        <button onClick={save} disabled={!hasChanges || saving}
          className="bg-orange-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
          <ArrowDownTrayIcon className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Classroom</th>
              {data.days.map(d => (
                <th key={d} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  <div>{DAYS[new Date(d).getDay()]}</div>
                  <div className="text-gray-400 font-normal">{new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</div>
                </th>
              ))}
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Capacity / Ratio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.rows.map(row => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-2">
                  <p className="font-medium text-gray-900">{row.name}</p>
                  {row.age_min_months != null && (
                    <p className="text-xs text-gray-400">{row.age_min_months}–{row.age_max_months} mo</p>
                  )}
                </td>
                {row.cells.map(cell => (
                  <td key={cell.date} className="px-2 py-2">
                    <input type="number" min="0"
                      value={draft[`${row.id}|${cell.date}`] ?? ''}
                      onChange={(e) => setCell(row.id, cell.date, e.target.value)}
                      placeholder="—"
                      className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-orange-500 focus:outline-none" />
                  </td>
                ))}
                <td className="px-3 py-2 text-xs text-gray-500">
                  cap {row.max_capacity || '—'} · 1:{row.max_kids_per_staff}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Tip: leave a cell blank if you have no forecast yet. The AI scheduler treats blanks as "use historical average".
      </p>
    </div>
  );
}
